/**
 * API client error handling for Stage Door Access.
 *
 * Public-facing surfaces must never render raw backend error messages — a
 * technical string like "Could not geocode the provided ZIP or postal code"
 * (shown during a geocoding-service outage) tells the studio their ZIP is bad
 * when the service is simply down. ApiClientError carries the backend error
 * code + HTTP status so the UI can map failures to friendly copy while the
 * original message is preserved for logging.
 */

/** Backend error envelope: `{ error: { code, message } }` (some paths use a top-level `message`). */
export interface ApiErrorEnvelope {
  error?: { code?: string; message?: string };
  message?: string;
}

/**
 * Error thrown by the API client layer when a request fails. Carries the HTTP
 * status and the backend error code (when present) so callers can classify the
 * failure without string-matching. `message` holds the raw backend message for
 * logging — do NOT render it directly on public surfaces; run it through
 * {@link getFriendlyErrorMessage} instead.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    // Restore the prototype chain so `instanceof ApiClientError` holds even
    // when compiled down to ES5-ish targets.
    Object.setPrototypeOf(this, ApiClientError.prototype);
  }
}

/**
 * Throw an ApiClientError from a failed response + parsed envelope. Centralizes
 * the repeated `if (!response.ok) throw new Error(result.error?.message || …)`
 * pattern across the public API modules.
 */
export function throwApiError(
  response: Response,
  result: ApiErrorEnvelope,
  fallback: string
): never {
  const message = result?.error?.message || result?.message || fallback;
  throw new ApiClientError(message, response.status, result?.error?.code);
}

const SERVER_ERROR = 'Something went wrong on our end. Please try again in a moment.';
const NETWORK_ERROR = "We couldn't reach the server. Check your connection and try again.";

/** Backend error code → user-facing copy. Keep aligned with EventDetailPage's ZIP banner. */
const FRIENDLY_BY_CODE: Record<string, string> = {
  SERVICE_UNAVAILABLE: 'Search is temporarily unavailable. Please try again in a few minutes.',
  INVALID_ZIP: "We couldn't find that location. Please enter a valid US ZIP or Canadian postal code.",
};

/**
 * Translate any thrown value into a message safe to show a public user.
 *
 * - Network/fetch failures (TypeError) → connection copy.
 * - Server errors (5xx) → generic "on our end" copy; technical detail stays server-side.
 * - Mapped codes (SERVICE_UNAVAILABLE, INVALID_ZIP) → purpose-written copy.
 * - Other 4xx → the backend message is passed through (client-error messages such
 *   as "You're already on the waitlist" are authored for users); `fallback` if empty.
 * - Anything else → the error's own message when present, otherwise `fallback`.
 */
export function getFriendlyErrorMessage(err: unknown, fallback: string): string {
  // fetch() rejects with a TypeError when the request never reaches the server
  // (offline, DNS failure, connection reset).
  if (err instanceof TypeError) {
    return NETWORK_ERROR;
  }

  if (err instanceof ApiClientError) {
    // Recognized codes get purpose-written copy first. SERVICE_UNAVAILABLE is a
    // 503, so this must win over the generic 5xx branch below — that is the whole
    // point of the incident fix (service down ≠ "your ZIP is invalid").
    const mapped = err.code ? FRIENDLY_BY_CODE[err.code] : undefined;
    if (mapped) {
      return mapped;
    }
    // Server-side failures must never surface technical detail to the public.
    if (err.status >= 500) {
      return SERVER_ERROR;
    }
    // Other 4xx: backend messages on client errors are authored for users
    // (e.g. "You're already on the waitlist"). Pass them through.
    if (err.status >= 400) {
      return err.message || fallback;
    }
    // Unknown/absent status — treat as a server hiccup rather than leaking detail.
    return SERVER_ERROR;
  }

  // Errors thrown by non-API layers (AuthContext, cart, etc.) — preserve any
  // authored message, otherwise fall back to the caller's copy.
  if (err instanceof Error && err.message) {
    return err.message;
  }

  return fallback;
}
